import { Component } from '@theme/component';
import { LICENSE_NAMED_CHANNEL_MAX_LENGTH } from '@theme/license-cart';
import {
  getActiveDigitalForProduct,
  parseCustomerLicenseSummary,
} from '@theme/license-eligibility';

/**
 * Footage license picker: Digital (always included) + optional Broadcast add-on.
 *
 * @typedef {object} LicenseVariantPickerRefs
 * @property {HTMLInputElement} broadcastCheckbox
 * @property {HTMLInputElement} namedChannelInput
 * @property {HTMLElement} namedChannelField
 * @property {HTMLElement} activeDigitalNotice
 * @property {HTMLElement} digitalLicenseBadge
 * @extends {Component<LicenseVariantPickerRefs>}
 */
class LicenseVariantPicker extends Component {
  requiredRefs = ['broadcastCheckbox', 'namedChannelInput', 'namedChannelField', 'activeDigitalNotice', 'digitalLicenseBadge'];

  connectedCallback() {
    super.connectedCallback();
    this.#syncVariantInput();
    this.#updatePriceDisplay();
    this.#updateCardState();
    this.#updateNamedChannelVisibility();
    this.#updateActiveDigitalNotice();
    this.#updateDigitalCardActiveState();
    this.#updateDigitalOnlyState();

    this.refs.broadcastCheckbox.addEventListener('change', this.#onBroadcastChange);
    this.refs.namedChannelInput.addEventListener('input', this.#onNamedChannelInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.broadcastCheckbox?.removeEventListener('change', this.#onBroadcastChange);
    this.refs.namedChannelInput?.removeEventListener('input', this.#onNamedChannelInput);
  }

  /** @returns {boolean} */
  includesBroadcast() {
    return this.refs.broadcastCheckbox.checked;
  }

  /** @returns {import('@theme/license-eligibility').CustomerLicenseSummary | null} */
  getLicenseSummary() {
    const sourceId = this.dataset.licenseSummarySource;
    if (sourceId) {
      const script = document.getElementById(sourceId);
      if (script?.textContent?.trim()) {
        return parseCustomerLicenseSummary(script.textContent.trim());
      }
    }

    return parseCustomerLicenseSummary(this.dataset.licenseSummary ?? null);
  }

  /** @returns {boolean} */
  hasActiveDigitalLicense() {
    if (this.dataset.customerLoggedIn !== 'true') return false;

    return Boolean(
      getActiveDigitalForProduct(this.getLicenseSummary(), this.dataset.productId ?? ''),
    );
  }

  /** @returns {string} */
  getNamedChannel() {
    return this.refs.namedChannelInput?.value ?? '';
  }

  focusNamedChannel() {
    this.refs.namedChannelInput?.focus();
  }

  /** @param {boolean} hasError */
  setNamedChannelError(hasError) {
    const input = this.refs.namedChannelInput;
    if (!input) return;

    input.classList.toggle('license-variant-picker__named-channel-input--error', hasError);

    if (hasError) {
      input.setAttribute('aria-invalid', 'true');
      return;
    }

    input.removeAttribute('aria-invalid');
  }

  /** @returns {boolean} */
  shouldBatchAdd() {
    return this.includesBroadcast();
  }

  /** @returns {boolean} */
  isAvailable() {
    if (this.dataset.digitalAvailable !== 'true') return false;
    if (this.includesBroadcast() && this.dataset.broadcastAvailable !== 'true') return false;
    return true;
  }

  /** Re-applies add-to-cart disabled state after an in-flight license add completes. */
  syncAddToCartButtonState() {
    this.#updateDigitalOnlyState();
  }

  #onBroadcastChange = () => {
    this.#syncVariantInput();
    this.#updatePriceDisplay();
    this.#updateCardState();
    this.#updateNamedChannelVisibility();
    this.#updateActiveDigitalNotice();
    this.#updateDigitalCardActiveState();
    this.#updateDigitalOnlyState();
    this.setNamedChannelError(false);
  };

  #updateDigitalOnlyState() {
    const section = this.closest('.shopify-section');
    const addToCartButton = section?.querySelector('[ref="addToCartButton"]');
    if (!(addToCartButton instanceof HTMLButtonElement)) return;

    const blockDigitalOnly =
      this.hasActiveDigitalLicense() && !this.includesBroadcast();

    addToCartButton.disabled = blockDigitalOnly;
    addToCartButton.setAttribute('aria-disabled', blockDigitalOnly ? 'true' : 'false');
  }

  #onNamedChannelInput = () => {
    this.setNamedChannelError(false);
  };

  #syncVariantInput() {
    const variantInput = this.#getVariantInput();
    if (!variantInput || !this.dataset.digitalVariantId) return;
    variantInput.value = this.dataset.digitalVariantId;
    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #updatePriceDisplay() {
    const priceContainer = this.#getPriceContainer();
    if (!priceContainer) return;

    const priceEl = priceContainer.querySelector('.price');
    if (!priceEl) return;

    let display = this.dataset.digitalPriceDisplay;

    if (this.includesBroadcast()) {
      display = this.hasActiveDigitalLicense()
        ? this.dataset.broadcastPriceDisplay
        : this.dataset.combinedPriceDisplay;
    }

    if (display) {
      priceEl.textContent = display;
    }
  }

  #updateActiveDigitalNotice() {
    const notice = this.refs.activeDigitalNotice;
    const activeDigital = this.hasActiveDigitalLicense()
      ? getActiveDigitalForProduct(this.getLicenseSummary(), this.dataset.productId ?? '')
      : null;

    if (!activeDigital) {
      notice.textContent = '';
      notice.hidden = true;
      notice.classList.add('hidden');
      return;
    }

    const label = this.dataset.activeDigitalLabel ?? 'Digital license active through';
    const expiresAt = new Date(activeDigital.expiresAt).toLocaleString();
    notice.textContent = `${label} ${expiresAt}`;
    notice.hidden = false;
    notice.classList.remove('hidden');
  }

  #updateDigitalCardActiveState() {
    const digitalCard = this.querySelector('[data-license-type="digital"]');
    const badge = this.refs.digitalLicenseBadge;
    const isActive = this.hasActiveDigitalLicense();

    if (digitalCard instanceof HTMLElement) {
      digitalCard.classList.toggle('license-variant-picker__card--digital-active', isActive);
    }

    if (!badge) return;

    badge.classList.toggle('license-variant-picker__badge--included', !isActive);
    badge.classList.toggle('license-variant-picker__badge--active', isActive);
    badge.textContent = isActive
      ? (this.dataset.badgeActiveLabel ?? 'Active')
      : (this.dataset.badgeIncludedLabel ?? 'Included');
  }

  #updateCardState() {
    const broadcastCard = this.querySelector('[data-license-type="broadcast"]');
    if (!(broadcastCard instanceof HTMLElement)) return;

    broadcastCard.classList.toggle(
      'license-variant-picker__card--selected',
      this.refs.broadcastCheckbox.checked
    );
  }

  #updateNamedChannelVisibility() {
    const { namedChannelField, namedChannelInput } = this.refs;
    const showField = this.includesBroadcast();

    namedChannelField.classList.toggle('hidden', !showField);
    namedChannelField.toggleAttribute('hidden', !showField);

    if (showField) {
      namedChannelInput.setAttribute('required', '');
      namedChannelInput.setAttribute('aria-required', 'true');
      return;
    }

    namedChannelInput.removeAttribute('required');
    namedChannelInput.removeAttribute('aria-required');
    namedChannelInput.value = '';
  }

  /** @returns {HTMLInputElement | null} */
  #getVariantInput() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('product-form-component input[name="id"]') ?? null;
  }

  /** @returns {HTMLElement | null} */
  #getPriceContainer() {
    const section = this.closest('.shopify-section');
    return section?.querySelector('product-price [ref="priceContainer"]') ?? null;
  }
}

if (!customElements.get('license-variant-picker')) {
  customElements.define('license-variant-picker', LicenseVariantPicker);
}

export { LICENSE_NAMED_CHANNEL_MAX_LENGTH };
export default LicenseVariantPicker;
